'use client';

import { useTranslation } from 'react-i18next';
import {
  useCurrentUser,
  useMicroSource,
  type MicroContentItem,
} from '@akademi/site-builder-runtime';
import { useAsyncResource } from '../_shared/useAsyncResource';
import { SectionShell } from '../_shared/SectionShell';
import { SkeletonRow } from '../_shared/SkeletonRow';
import { AdapterPlaceholder } from '../_shared/AdapterPlaceholder';
import { ContentRail } from '../ui/ContentRail';
import { MicroItemCard } from './_MicroItemCard';
import { useMicroCardDecorations } from './_useMicroCardDecorations';

/**
 * Personalized micro-content recommendations. Sourced from
 * {@code MicroSource.recommendations.fetch}. Hides itself silently
 * when there's no user, no adapter, or no items — operators don't
 * want a phantom rail on a fresh-user page.
 */
export interface MicroRecommendationRailProps {
  title?: string;
  limit?: number;
  surface?: string;
  itemHrefTemplate?: string;
  /** Show the content-kind chip over each card's cover. */
  showKindBadge?: boolean;
}

export function MicroRecommendationRail({
  title: authoredTitle,
  limit = 8,
  surface,
  itemHrefTemplate,
  showKindBadge = true,
}: MicroRecommendationRailProps) {
  const { t } = useTranslation('site-builder-blocks');
  const title = authoredTitle ?? t('microRecommendations.title');
  const user = useCurrentUser<{ name?: string }>();
  const micro = useMicroSource();
  const decorations = useMicroCardDecorations();

  const state = useAsyncResource<MicroContentItem[]>(
    async (signal) => {
      if (!user) return null;
      if (!micro?.recommendations) return null;
      const args: Parameters<NonNullable<typeof micro.recommendations>['fetch']>[0] = {
        limit,
        signal,
      };
      if (surface) args.surface = surface;
      const items = await micro.recommendations.fetch(args);
      return items.length > 0 ? items : null;
    },
    [user, micro, limit, surface],
  );

  if (!user) return null;

  if (!micro?.recommendations) {
    return (
      <SectionShell title={title}>
        <AdapterPlaceholder
          blockType="MicroRecommendationRail"
          adapterName="MicroSource.recommendations"
        />
      </SectionShell>
    );
  }

  if (state.kind === 'loading') {
    return (
      <SectionShell title={title}>
        <SkeletonRow count={limit} layout="rail" />
      </SectionShell>
    );
  }

  if (state.kind !== 'loaded') return null;

  return (
    <SectionShell title={title}>
      <ContentRail ariaLabel={title} itemWidth="240px" snap="start">
        {state.data.map((item) => (
          <MicroItemCard
            showKindBadge={showKindBadge}
            key={item.id}
            item={item}
            progress={decorations.progressFor(item.id)}
            categoryName={decorations.categoryNameFor(item.categoryId)}
            {...(itemHrefTemplate ? { hrefTemplate: itemHrefTemplate } : {})}
          />
        ))}
      </ContentRail>
    </SectionShell>
  );
}
